import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, collection, getDocs, updateDoc, deleteDoc, query, orderBy, onSnapshot, collectionGroup, where, serverTimestamp, addDoc, limit } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { FaceBoomLogo } from './svg_utilities.jsx';
import {
    SUPPORT_CATEGORIES,
    SUPPORT_PRIORITIES,
    SUPPORT_STATUS_LABELS,
    SUPPORT_STATUSES,
    addAdminNote,
    addSupportMessage,
    subscribeAdminNotes,
    subscribeAdminTickets,
    subscribeTicketMessages,
    updateSupportTicketStatus,
} from './support-service.js';
import { SupportToast, statusBadgeClass } from './support-ui.jsx';

// --- PASTE FIREBASE CONFIG & OTHER SHARED ITEMS HERE ---
// In a larger app, you'd share this from a config file.
const firebaseConfig = {
  apiKey: "AIzaSyAAzF9I2qPsiesJp0NFX6TRYzimIKlziLc",
  authDomain: "faceboom-ai-headshots.firebaseapp.com",
  projectId: "faceboom-ai-headshots",
  storageBucket: "faceboom-ai-headshots.firebasestorage.app",
  messagingSenderId: "73519928036",
  appId: "1:73519928036:web:6967919289d080786312f1",
  measurementId: "G-2Q711C9QHW"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

const DEFAULT_GEMINI_IMAGE_MODEL = "gemini-2.5-flash-image";
const DEFAULT_GEMINI_VISION_MODEL = "gemini-2.5-flash";
const DEFAULT_PRICE_PER_IMAGE = "0.139";

const cloudFunctionUrl =
    "https://us-central1-faceboom-ai-headshots.cloudfunctions.net";

function MaterialSymbol({ name, className = "", ariaHidden = true }) {
    return (
        <span
            className={`material-symbols-rounded ${className}`.trim()}
            aria-hidden={ariaHidden}
        >
            {name}
        </span>
    );
}

// --- Main Admin Component ---
export default function AdminPage({ onNavigate }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [viewingUser, setViewingUser] = useState(null);
    const [dashboardStats, setDashboardStats] = useState({
        totalUsers: 0,
        totalImagesGenerated: 0,
        totalCost: 0,
        totalIncome: 0,
        totalProfit: 0,
        totalStripeFees: 0,
        activeSubscribers: 0,
        systemLoad: 0,
    });
    const [kpiTotals, setKpiTotals] = useState({ totalCost: 0 });
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
    const [imageModel, setImageModel] = useState(DEFAULT_GEMINI_IMAGE_MODEL);
    const [visionModel, setVisionModel] = useState(DEFAULT_GEMINI_VISION_MODEL);
    const [modelConfigLoading, setModelConfigLoading] = useState(false);
    const [modelConfigError, setModelConfigError] = useState(null);
    const [isSavingModels, setIsSavingModels] = useState(false);
    const [modelSaveStatus, setModelSaveStatus] = useState('');
    const [modelList, setModelList] = useState([]);
    const [modelListError, setModelListError] = useState(null);
    const [modelListWarning, setModelListWarning] = useState(null);
    const [modelListLoading, setModelListLoading] = useState(false);
    const [modelRawResponse, setModelRawResponse] = useState(null);
    const [modelRawError, setModelRawError] = useState(null);
    const [modelRawLoading, setModelRawLoading] = useState(false);
    const [modelRawWarning, setModelRawWarning] = useState(null);
    const [pricePerImage, setPricePerImage] = useState(DEFAULT_PRICE_PER_IMAGE);
    const [priceConfigLoading, setPriceConfigLoading] = useState(false);
    const [priceConfigError, setPriceConfigError] = useState(null);
    const [priceSaveStatus, setPriceSaveStatus] = useState('');
    const [isSavingPrice, setIsSavingPrice] = useState(false);
    const [activeSection, setActiveSection] = useState('dashboard');
    const [devtoolsOutput, setDevtoolsOutput] = useState('');
    const [devtoolsStatus, setDevtoolsStatus] = useState('');
    const [supportTickets, setSupportTickets] = useState([]);
    const [supportLoading, setSupportLoading] = useState(false);
    const [supportError, setSupportError] = useState(null);
    const [supportFilters, setSupportFilters] = useState({
        status: 'all',
        priority: 'all',
        category: 'all',
        search: '',
    });
    const [selectedSupportTicketId, setSelectedSupportTicketId] = useState(null);
    const [supportMessages, setSupportMessages] = useState([]);
    const [supportMessagesLoading, setSupportMessagesLoading] = useState(false);
    const [supportMessagesError, setSupportMessagesError] = useState(null);
    const [supportReplyBody, setSupportReplyBody] = useState('');
    const [supportNotes, setSupportNotes] = useState([]);
    const [supportNotesLoading, setSupportNotesLoading] = useState(false);
    const [supportNoteBody, setSupportNoteBody] = useState('');
    const [supportActionLoading, setSupportActionLoading] = useState(false);
    const [supportToast, setSupportToast] = useState(null);
    const [supportBadgeCount, setSupportBadgeCount] = useState(0);
    const [auditLogs, setAuditLogs] = useState([]);
    const [auditLogsLoading, setAuditLogsLoading] = useState(false);
    const [auditLogsError, setAuditLogsError] = useState(null);
    const [playgroundToast, setPlaygroundToast] = useState(null);
    const [showPlaygroundModal, setShowPlaygroundModal] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isPlaygroundLoading, setIsPlaygroundLoading] = useState(false);
    const [playgroundAccordion, setPlaygroundAccordion] = useState(0);
    const [playgroundStep, setPlaygroundStep] = useState(2);
    const [playgroundForm, setPlaygroundForm] = useState({
        name: '',
        email: '',
        status: 'active',
        message: '',
        password: '',
        notifications: true,
        radioChoice: 'option-a',
        sliderValue: 45,
    });

    // --- Data Fetching ---
    const parsePriceValue = (value) => {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const pricePerImageRef = useRef(pricePerImage);
    const modelConfigRef = useRef({
        gemini_image_model: DEFAULT_GEMINI_IMAGE_MODEL,
        gemini_vision_model: DEFAULT_GEMINI_VISION_MODEL,
    });
    const priceConfigRef = useRef(DEFAULT_PRICE_PER_IMAGE);

    useEffect(() => {
        pricePerImageRef.current = pricePerImage;
    }, [pricePerImage]);

    useEffect(() => {
        if (!showPlaygroundModal && !showConfirmDialog) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setShowPlaygroundModal(false);
                setShowConfirmDialog(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showPlaygroundModal, showConfirmDialog]);

    const handlePlaygroundLoading = () => {
        setIsPlaygroundLoading(true);
        window.setTimeout(() => {
            setIsPlaygroundLoading(false);
        }, 1200);
    };

    const fetchUsers = useCallback(async (priceOverride) => {
        try {
            const usersCollectionRef = collection(db, 'users');
            const userSnapshot = await getDocs(usersCollectionRef);
            const userList = userSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Calculate active subscribers
            const activeSubscribers = userList.filter(user => user.plan === 'Premium').length;

            const totalImages = userList.reduce((sum, user) => {
                const imagesGenerated = parseInt(user.images_generated, 10) || 0;
                return sum + imagesGenerated;
            }, 0);

            const priceValue = Number.isFinite(priceOverride)
                ? priceOverride
                : parsePriceValue(pricePerImageRef.current);
            const totalCost = totalImages * priceValue;

            // Fetch total income from successful payments
            const paymentsQuery = query(collectionGroup(db, 'payments'), where('status', '==', 'succeeded'));
            const paymentSnapshot = await getDocs(paymentsQuery);
            let totalIncomeCents = 0;
            let totalStripeFeesCents = 0;
            
            paymentSnapshot.forEach(doc => {
                const amount = doc.data().amount || 0;
                totalIncomeCents += amount;
                // Stripe fee per transaction: 2.9% of amount + 30 cents
                const feeForThisTransaction = (amount * 0.029) + 30;
                totalStripeFeesCents += feeForThisTransaction;
            });

            const totalIncome = totalIncomeCents / 100;
            const totalStripeFees = totalStripeFeesCents / 100;
            const totalProfit = totalIncome - totalCost - totalStripeFees;
            const systemLoad = userList.reduce((sum, user) => {
                const taskArray = Array.isArray(user.generation_tasks)
                    ? user.generation_tasks
                    : Array.isArray(user.generationTasks)
                        ? user.generationTasks
                        : null;
                if (taskArray) {
                    return sum + taskArray.filter((task) => task?.status === 'active').length;
                }
                const numericValue = Number(
                    user.active_generation_tasks ??
                    user.activeGenerationTasks ??
                    user.activeTaskCount ??
                    0
                );
                return sum + (Number.isFinite(numericValue) ? numericValue : 0);
            }, 0);

            setUsers(userList);
            setDashboardStats({ 
                totalUsers: userList.length, 
                totalImagesGenerated: totalImages, 
                totalCost: totalCost,
                totalIncome: totalIncome,
                totalProfit: totalProfit,
                totalStripeFees: totalStripeFees,
                activeSubscribers: activeSubscribers,
                systemLoad: systemLoad
            });
        } catch (err) {
            console.error("Error fetching users:", err);
            setError("Failed to load user data.");
        }
    }, []);

    // --- Authentication and Role Check ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userDocRef = doc(db, 'users', user.uid);
                const docSnap = await getDoc(userDocRef);
                if (docSnap.exists() && docSnap.data().role === 'admin') {
                    setCurrentUser(user);
                    setIsAdmin(true);
                    fetchUsers();
                } else {
                    setIsAdmin(false);
                    setError("Access Denied. You must be an administrator to view this page.");
                }
            } else {
                setIsAdmin(false);
                setError("Please log in to continue.");
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, [fetchUsers]);

    const loadModelConfig = useCallback(async () => {
        if (!isAdmin) return;

        setModelConfigLoading(true);
        setModelConfigError(null);
        try {
            const configRef = doc(db, 'app_config', 'models');
            const snapshot = await getDoc(configRef);
            const data = snapshot.exists() ? snapshot.data() : {};
            const imageValue = typeof data?.gemini_image_model === 'string' && data.gemini_image_model.trim()
                ? data.gemini_image_model.trim()
                : DEFAULT_GEMINI_IMAGE_MODEL;
            const visionValue = typeof data?.gemini_vision_model === 'string' && data.gemini_vision_model.trim()
                ? data.gemini_vision_model.trim()
                : DEFAULT_GEMINI_VISION_MODEL;
            setImageModel(imageValue);
            setVisionModel(visionValue);
            modelConfigRef.current = {
                gemini_image_model: imageValue,
                gemini_vision_model: visionValue,
            };
        } catch (err) {
            console.error("Failed to load Gemini model config:", err);
            setModelConfigError("Failed to load Gemini model config.");
        } finally {
            setModelConfigLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        loadModelConfig();
    }, [loadModelConfig]);

    const loadModelList = useCallback(async () => {
        if (!isAdmin || !currentUser) return;

        setModelListLoading(true);
        setModelListError(null);
        setModelListWarning(null);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${cloudFunctionUrl}/listGeminiModels`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch model list (${response.status}).`);
            }
            const data = await response.json();
            setModelList(Array.isArray(data?.models) ? data.models : []);
            if (data?.stale) {
                setModelListWarning("Model list is stale; showing cached results.");
            }
        } catch (err) {
            console.error("Failed to fetch Gemini model list:", err);
            setModelList([]);
            setModelListError("Unable to fetch model list; showing current only.");
        } finally {
            setModelListLoading(false);
        }
    }, [currentUser, isAdmin]);

    useEffect(() => {
        loadModelList();
    }, [loadModelList]);

    const loadRawModelList = useCallback(async () => {
        if (!isAdmin || !currentUser) return;

        setModelRawLoading(true);
        setModelRawError(null);
        setModelRawWarning(null);
        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(`${cloudFunctionUrl}/listGeminiModelsRaw`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error(`Failed to fetch raw model list (${response.status}).`);
            }
            const data = await response.json();
            setModelRawResponse(data);
            setDevtoolsOutput(JSON.stringify(data?.response ?? data, null, 2));
            if (data?.stale) {
                setModelRawWarning("Raw model list is stale; showing cached results.");
            }
        } catch (err) {
            console.error("Failed to fetch raw Gemini model list:", err);
            setModelRawResponse(null);
            setModelRawError("Unable to fetch raw model list.");
            setDevtoolsOutput('');
        } finally {
            setModelRawLoading(false);
        }
    }, [currentUser, isAdmin]);

    useEffect(() => {
        if (!isAdmin) return undefined;
        setSupportLoading(true);
        setSupportError(null);
        const statusFilter =
            supportFilters.status === 'all' ||
            supportFilters.status === SUPPORT_STATUSES.AWAITING_SUPPORT
                ? null
                : supportFilters.status;
        const unsubscribe = subscribeAdminTickets({
            db,
            statusFilter,
            onData: (snapshot) => {
                const nextTickets = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                }));
                setSupportTickets(nextTickets);
                if (nextTickets.length > 0) {
                    setSelectedSupportTicketId((current) => current ?? nextTickets[0].id);
                }
                setSupportLoading(false);
            },
            onError: (error) => {
                console.error('Failed to load support tickets:', error);
                setSupportError('Unable to load support tickets.');
                setSupportLoading(false);
            },
        });
        return () => unsubscribe();
    }, [isAdmin, supportFilters.status]);

    useEffect(() => {
        if (!isAdmin) return undefined;
        const ticketsRef = collection(db, 'support_tickets');
        const badgeQuery = query(ticketsRef, where('lastMessageBy', '==', 'user'));
        const unsubscribe = onSnapshot(
            badgeQuery,
            (snapshot) => {
                const count = snapshot.docs.filter((docSnap) => {
                    const data = docSnap.data();
                    return (
                        data.status !== SUPPORT_STATUSES.CLOSED &&
                        data.status !== SUPPORT_STATUSES.RESOLVED
                    );
                }).length;
                setSupportBadgeCount(count);
            },
            (error) => console.error('Failed to load support badge:', error)
        );
        return () => unsubscribe();
    }, [isAdmin]);

    useEffect(() => {
        if (!selectedSupportTicketId) {
            setSupportMessages([]);
            return undefined;
        }
        setSupportMessagesLoading(true);
        setSupportMessagesError(null);
        const unsubscribe = subscribeTicketMessages({
            db,
            ticketId: selectedSupportTicketId,
            onData: (snapshot) => {
                setSupportMessages(
                    snapshot.docs.map((docSnap) => ({
                        id: docSnap.id,
                        ...docSnap.data(),
                    }))
                );
                setSupportMessagesLoading(false);
            },
            onError: (error) => {
                console.error('Failed to load support messages:', error);
                setSupportMessagesError('Unable to load support messages.');
                setSupportMessagesLoading(false);
            },
        });
        return () => unsubscribe();
    }, [selectedSupportTicketId]);

    useEffect(() => {
        if (!selectedSupportTicketId) {
            setSupportNotes([]);
            return undefined;
        }
        setSupportNotesLoading(true);
        const unsubscribe = subscribeAdminNotes({
            db,
            ticketId: selectedSupportTicketId,
            onData: (snapshot) => {
                setSupportNotes(
                    snapshot.docs.map((docSnap) => ({
                        id: docSnap.id,
                        ...docSnap.data(),
                    }))
                );
                setSupportNotesLoading(false);
            },
            onError: (error) => {
                console.error('Failed to load support notes:', error);
                setSupportNotesLoading(false);
            },
        });
        return () => unsubscribe();
    }, [selectedSupportTicketId]);

    useEffect(() => {
        if (!isAdmin) return undefined;

        setPriceConfigLoading(true);
        setPriceConfigError(null);
        const configRef = doc(db, 'app_config', 'KPIs');
        const unsubscribe = onSnapshot(
            configRef,
            async (snapshot) => {
                const data = snapshot.exists() ? snapshot.data() : {};
                const rawPrice = typeof data?.total_cost === 'number'
                    ? data.total_cost.toString()
                    : (typeof data?.total_cost === 'string' ? data.total_cost : DEFAULT_PRICE_PER_IMAGE);
                const parsedTotalCost = parsePriceValue(rawPrice);
                setPricePerImage(rawPrice);
                setKpiTotals({ totalCost: parsedTotalCost });
                priceConfigRef.current = rawPrice;
                await fetchUsers(parsedTotalCost);
                setPriceConfigLoading(false);
            },
            (err) => {
                console.error("Failed to load KPI config:", err);
                setPriceConfigError("Failed to load KPI config.");
                setPriceConfigLoading(false);
            }
        );
        return () => unsubscribe();
    }, [fetchUsers, isAdmin]);

    useEffect(() => {
        if (!isAdmin) return undefined;
        setAuditLogsLoading(true);
        setAuditLogsError(null);
        const logsRef = collection(db, 'admin_audit_logs');
        const logsQuery = query(logsRef, orderBy('timestamp', 'desc'), limit(50));
        const unsubscribe = onSnapshot(
            logsQuery,
            (snapshot) => {
                setAuditLogs(snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data(),
                })));
                setAuditLogsLoading(false);
            },
            (error) => {
                console.error('Failed to load audit logs:', error);
                setAuditLogsError('Unable to load audit logs.');
                setAuditLogsLoading(false);
            }
        );
        return () => unsubscribe();
    }, [isAdmin]);

    const logAdminAction = useCallback(async ({ actionType, targetUserId, oldValue, newValue }) => {
        if (!currentUser) return;
        try {
            await addDoc(collection(db, 'admin_audit_logs'), {
                adminId: currentUser.uid,
                actionType,
                targetUserId,
                oldValue,
                newValue,
                timestamp: serverTimestamp(),
            });
        } catch (err) {
            console.error('Failed to log admin action:', err);
        }
    }, [currentUser]);

    const sortedAndFilteredUsers = useMemo(() => {
        let sortableUsers = [...users];
        
        if (sortConfig.key) {
            sortableUsers.sort((a, b) => {
                let aValue = a[sortConfig.key];
                let bValue = b[sortConfig.key];

                if (aValue === undefined || aValue === null) return 1;
                if (bValue === undefined || bValue === null) return -1;
                
                if (['credits', 'images_generated'].includes(sortConfig.key)) {
                    aValue = parseInt(aValue, 10) || 0;
                    bValue = parseInt(bValue, 10) || 0;
                }

                if (aValue.toDate && bValue.toDate) {
                     if (aValue.toDate() < bValue.toDate()) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (aValue.toDate() > bValue.toDate()) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                }

                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }

        if (!searchQuery) {
            return sortableUsers;
        }

        return sortableUsers.filter(user =>
            user.email.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [users, sortConfig, searchQuery]);
    
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const formatModelLabel = (model) => {
        if (!model?.id) return '';
        const displayName = typeof model.displayName === 'string' ? model.displayName.trim() : '';
        if (displayName && displayName !== model.id) {
            return `${displayName} - (${model.id})`;
        }
        return model.id;
    };

    const imageModelOptions = useMemo(() => {
        const options = [];
        const seen = new Set();
        const imageModels = modelList.filter(model => model?.supports?.image);

        for (const model of imageModels) {
            if (!model?.id || seen.has(model.id)) continue;
            seen.add(model.id);
            options.push({
                value: model.id,
                label: formatModelLabel(model),
            });
        }

        if (imageModel && !seen.has(imageModel)) {
            options.unshift({ value: imageModel, label: `${imageModel} (current)` });
        }

        return options;
    }, [imageModel, modelList]);

    const visionModelOptions = useMemo(() => {
        const options = [];
        const seen = new Set();
        const visionModels = modelList.filter(model => model?.supports?.vision);

        for (const model of visionModels) {
            if (!model?.id || seen.has(model.id)) continue;
            seen.add(model.id);
            options.push({
                value: model.id,
                label: formatModelLabel(model),
            });
        }

        if (visionModel && !seen.has(visionModel)) {
            options.unshift({ value: visionModel, label: `${visionModel} (current)` });
        }

        return options;
    }, [visionModel, modelList]);

    const visionModelApiEmpty = useMemo(() => {
        if (!modelList.length) return false;
        return modelList.every(model => !model?.supports?.vision);
    }, [modelList]);

    const handleSaveModels = async () => {
        if (!currentUser) return;
        setIsSavingModels(true);
        setModelSaveStatus('');
        try {
            const configRef = doc(db, 'app_config', 'models');
            await setDoc(configRef, {
                gemini_image_model: imageModel,
                gemini_vision_model: visionModel,
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid,
            }, { merge: true });
            await logAdminAction({
                actionType: 'model_config_update',
                targetUserId: 'app_config/models',
                oldValue: modelConfigRef.current,
                newValue: {
                    gemini_image_model: imageModel,
                    gemini_vision_model: visionModel,
                },
            });
            modelConfigRef.current = {
                gemini_image_model: imageModel,
                gemini_vision_model: visionModel,
            };
            setModelSaveStatus('Saved');
            setTimeout(() => setModelSaveStatus(''), 2500);
        } catch (err) {
            console.error("Failed to save Gemini model config:", err);
            setModelSaveStatus('Save failed');
        } finally {
            setIsSavingModels(false);
        }
    };

    const handleSavePrice = async () => {
        if (!currentUser) return;
        setIsSavingPrice(true);
        setPriceSaveStatus('');
        try {
            const configRef = doc(db, 'app_config', 'KPIs');
            await setDoc(configRef, {
                total_cost: pricePerImage,
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.uid,
            }, { merge: true });
            await logAdminAction({
                actionType: 'price_config_update',
                targetUserId: 'app_config/KPIs',
                oldValue: { total_cost: priceConfigRef.current },
                newValue: { total_cost: pricePerImage },
            });
            priceConfigRef.current = pricePerImage;
            setPriceSaveStatus('Saved');
            await fetchUsers(parsePriceValue(pricePerImage));
            setTimeout(() => setPriceSaveStatus(''), 2500);
        } catch (err) {
            console.error("Failed to save KPI config:", err);
            setPriceSaveStatus('Save failed');
        } finally {
            setIsSavingPrice(false);
        }
    };

    const handleDevtoolsRefreshUsers = async () => {
        setDevtoolsStatus('Refreshing dashboard metrics...');
        await fetchUsers();
        setDevtoolsStatus('Dashboard metrics refreshed.');
    };

    const handleDevtoolsReloadModels = async () => {
        setDevtoolsStatus('Refreshing Gemini model list...');
        await loadModelList();
        setDevtoolsStatus('Gemini model list refreshed.');
    };

    const handleDevtoolsFetchRawModels = async () => {
        setDevtoolsStatus('Fetching raw Gemini model response...');
        await loadRawModelList();
        setDevtoolsStatus('Raw model response loaded.');
    };

    const handleDevtoolsCheckConfig = () => {
        const imageModelSupported = imageModelOptions.some(option => option.value === imageModel);
        const visionModelSupported = visionModelOptions.some(option => option.value === visionModel);
        const details = {
            imageModel,
            visionModel,
            imageModelSupported,
            visionModelSupported,
            totalModels: modelList.length,
            timestamp: new Date().toISOString(),
        };
        setDevtoolsOutput(JSON.stringify(details, null, 2));
        setDevtoolsStatus('Model config check complete.');
    };

    const handleDevtoolsClear = () => {
        setDevtoolsOutput('');
        setDevtoolsStatus('Console cleared.');
    };

    const getDisplayStatus = (ticket) => {
        if (!ticket) return SUPPORT_STATUSES.OPEN;
        if (
            ticket.status === SUPPORT_STATUSES.RESOLVED ||
            ticket.status === SUPPORT_STATUSES.CLOSED
        ) {
            return ticket.status;
        }
        if (ticket.lastMessageBy === 'admin') {
            return SUPPORT_STATUSES.AWAITING_USER;
        }
        if (ticket.lastMessageBy === 'user') {
            return SUPPORT_STATUSES.AWAITING_SUPPORT;
        }
        return ticket.status || SUPPORT_STATUSES.OPEN;
    };

    const filteredSupportTickets = useMemo(() => {
        const search = supportFilters.search.trim().toLowerCase();
        return supportTickets.filter((ticket) => {
            const displayStatus = getDisplayStatus(ticket);
            if (
                supportFilters.status !== 'all' &&
                displayStatus !== supportFilters.status
            ) {
                return false;
            }
            if (
                supportFilters.priority !== 'all' &&
                ticket.priority !== supportFilters.priority
            ) {
                return false;
            }
            if (
                supportFilters.category !== 'all' &&
                ticket.category !== supportFilters.category
            ) {
                return false;
            }
            if (search) {
                const haystack = `${ticket.subject || ''} ${ticket.userEmail || ''} ${
                    ticket.userDisplayName || ''
                }`.toLowerCase();
                return haystack.includes(search);
            }
            return true;
        });
    }, [supportFilters, supportTickets]);

    const selectedSupportTicket = supportTickets.find(
        (ticket) => ticket.id === selectedSupportTicketId
    );
    const selectedSupportDisplayStatus = getDisplayStatus(selectedSupportTicket);

    const formatTimestamp = (timestamp) => {
        const date = timestamp?.toDate?.();
        return date ? date.toLocaleString() : '—';
    };

    const handleSupportReply = async () => {
        if (!selectedSupportTicket || !currentUser) return;
        if (!supportReplyBody.trim()) {
            setSupportToast({ tone: 'error', message: 'Reply cannot be empty.' });
            return;
        }
        setSupportActionLoading(true);
        try {
            await addSupportMessage({
                db,
                ticketId: selectedSupportTicket.id,
                authorType: 'admin',
                authorId: currentUser.uid,
                body: supportReplyBody.trim(),
                status: SUPPORT_STATUSES.AWAITING_USER,
            });
            setSupportReplyBody('');
            setSupportToast({ tone: 'success', message: 'Reply sent to user.' });
        } catch (error) {
            console.error('Failed to send support reply:', error);
            setSupportToast({ tone: 'error', message: 'Failed to send reply.' });
        } finally {
            setSupportActionLoading(false);
        }
    };

    const handleSupportStatusChange = async (status) => {
        if (!selectedSupportTicket || !currentUser) return;
        setSupportActionLoading(true);
        try {
            await updateSupportTicketStatus({
                db,
                ticketId: selectedSupportTicket.id,
                status,
                actorType: 'admin',
            });
            setSupportToast({ tone: 'success', message: 'Ticket status updated.' });
        } catch (error) {
            console.error('Failed to update ticket status:', error);
            setSupportToast({ tone: 'error', message: 'Unable to update status.' });
        } finally {
            setSupportActionLoading(false);
        }
    };

    const handleAssignToSelf = async () => {
        if (!selectedSupportTicket || !currentUser) return;
        setSupportActionLoading(true);
        try {
            const ticketRef = doc(db, 'support_tickets', selectedSupportTicket.id);
            await updateDoc(ticketRef, {
                assignedAdminId: currentUser.uid,
                updatedAt: serverTimestamp(),
            });
            setSupportToast({ tone: 'success', message: 'Assigned to you.' });
        } catch (error) {
            console.error('Failed to assign ticket:', error);
            setSupportToast({ tone: 'error', message: 'Unable to assign ticket.' });
        } finally {
            setSupportActionLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!selectedSupportTicket || !currentUser) return;
        if (!supportNoteBody.trim()) {
            setSupportToast({ tone: 'error', message: 'Note cannot be empty.' });
            return;
        }
        setSupportActionLoading(true);
        try {
            await addAdminNote({
                db,
                ticketId: selectedSupportTicket.id,
                authorId: currentUser.uid,
                body: supportNoteBody.trim(),
            });
            setSupportNoteBody('');
            setSupportToast({ tone: 'success', message: 'Internal note added.' });
        } catch (error) {
            console.error('Failed to add note:', error);
            setSupportToast({ tone: 'error', message: 'Unable to add note.' });
        } finally {
            setSupportActionLoading(false);
        }
    };

    const adminSections = useMemo(() => ([
        {
            id: 'dashboard',
            label: 'Dashboard',
            description: 'Overview, KPIs, and quick insights.',
            icon: 'dvr',
        },
        {
            id: 'users',
            label: 'Users',
            description: 'User management and account oversight.',
            icon: 'manage_accounts',
        },
        {
            id: 'models',
            label: 'Configuration',
            description: 'Gemini models and KPI settings.',
            icon: 'instant_mix',
        },
        {
            id: 'devtools',
            label: 'Devtools',
            description: 'Admin debug console and diagnostics.',
            icon: 'code_blocks',
        },
        {
            id: 'support',
            label: 'Support',
            description: 'Ticket inbox and customer responses.',
            icon: 'support_agent',
        },
        {
            id: 'audit',
            label: 'Audit Logs',
            description: 'Track admin actions and configuration updates.',
            icon: 'manage_search',
        },
        {
            id: 'playground',
            label: 'UI/UX Playground',
            description: 'Reusable components and layout sandbox.',
            icon: 'science',
        },
    ]), []);

    const playgroundTableRows = [
        { id: 'USR-1024', name: 'Avery Matthews', status: 'Active', role: 'Creator' },
        { id: 'USR-2048', name: 'Jordan Lee', status: 'Pending', role: 'Editor' },
        { id: 'USR-4096', name: 'Morgan Diaz', status: 'Disabled', role: 'Viewer' },
    ];
    const playgroundIcons = [
        'dashboard',
        'support_agent',
        'photo_library',
        'manage_accounts',
        'insights',
        'instant_mix',
        'palette',
        'bolt',
        'shield_person',
        'tune',
        'science',
        'search',
    ];

    const activeSectionMeta = adminSections.find((section) => section.id === activeSection) || adminSections[0];
    const {
        averageImagesPerUser,
        clv,
        profitMargin,
        burnRatePerUser,
        breakEvenNet,
        targetForProfit,
    } = useMemo(() => {
        const totalUsers = dashboardStats.totalUsers;
        const averageImages = totalUsers ? dashboardStats.totalImagesGenerated / totalUsers : 0;
        const customerLifetimeValue = totalUsers ? dashboardStats.totalIncome / totalUsers : 0;
        const margin = dashboardStats.totalIncome
            ? (dashboardStats.totalProfit / dashboardStats.totalIncome) * 100
            : 0;
        const burnRate = totalUsers ? kpiTotals.totalCost / totalUsers : 0;
        const netProfit = dashboardStats.totalIncome - dashboardStats.totalStripeFees - kpiTotals.totalCost;
        const averageNetRevenuePerUser = totalUsers
            ? (dashboardStats.totalIncome - dashboardStats.totalStripeFees) / totalUsers
            : 0;
        const target = netProfit < 0 && averageNetRevenuePerUser > 0
            ? Math.ceil(Math.abs(netProfit) / averageNetRevenuePerUser)
            : 0;
        return {
            averageImagesPerUser: averageImages,
            clv: customerLifetimeValue,
            profitMargin: margin,
            burnRatePerUser: burnRate,
            breakEvenNet: netProfit,
            targetForProfit: target,
        };
    }, [
        dashboardStats.totalUsers,
        dashboardStats.totalImagesGenerated,
        dashboardStats.totalIncome,
        dashboardStats.totalProfit,
        dashboardStats.totalStripeFees,
        kpiTotals.totalCost,
    ]);
    const handleImpersonate = (user) => {
        localStorage.setItem('admin_impersonating_id', user.id);
        window.location.assign('/');
    };

    const formatChangeDescription = (entry) => {
        const { oldValue, newValue } = entry || {};
        if (!oldValue && !newValue) return '—';
        const formatValue = (value) => {
            if (value === undefined || value === null) return '—';
            if (typeof value === 'string' || typeof value === 'number') return `${value}`;
            if (typeof value === 'boolean') return value ? 'Yes' : 'No';
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
        };
        if (typeof oldValue !== 'object' || typeof newValue !== 'object') {
            return `${formatValue(oldValue)} -> ${formatValue(newValue)}`;
        }
        const keys = Array.from(new Set([
            ...Object.keys(oldValue || {}),
            ...Object.keys(newValue || {}),
        ]));
        const changes = keys.map((key) => {
            const previous = oldValue?.[key];
            const next = newValue?.[key];
            if (previous === next) return null;
            const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            return `${label}: ${formatValue(previous)} -> ${formatValue(next)}`;
        }).filter(Boolean);
        return changes.length ? changes.join(' • ') : '—';
    };

    const actionBadgeClass = (actionType) => {
        switch (actionType) {
            case 'user_update':
                return 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/40';
            case 'model_config_update':
                return 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/40';
            case 'price_config_update':
                return 'bg-amber-500/20 text-amber-200 border border-amber-400/40';
            default:
                return 'bg-gray-700/60 text-gray-200 border border-gray-600';
        }
    };

    if (loading) {
        return <div className="min-h-screen bg-gray-900 flex items-center justify-center text-white"><p>Verifying permissions...</p></div>;
    }

    if (!isAdmin) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4">
                <div className="text-center bg-gray-800 p-8 rounded-lg shadow-xl">
                    <h1 className="text-3xl font-bold text-red-500 mb-4">Access Denied</h1>
                    <p className="text-gray-400 mb-6">{error}</p>
                    <button onClick={() => onNavigate('app')} className="bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-2 px-6 rounded-lg">Go to App</button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-900 text-white font-sans flex flex-col">
            <header className="bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
                <div className="flex items-center gap-4">
                    <FaceBoomLogo className="h-8 w-auto" />
                    <h1 className="text-2xl font-bold">Admin Panel</h1>
                </div>
                <button onClick={() => onNavigate('app')} className="text-sm bg-gray-600 hover:bg-gray-500 text-white font-semibold py-2 px-4 rounded-lg">&larr; Back to Generator</button>
            </header>

            <div className="flex flex-1 flex-col md:flex-row">
                <aside className="w-full md:w-64 bg-gray-800/60 border-b md:border-b-0 md:border-r border-gray-700">
                    <div className="p-5">
                        <nav className="space-y-2">
                            {adminSections.map((section) => (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveSection(section.id)}
                                    className={`w-full text-left px-4 py-3 rounded-lg transition ${
                                        activeSection === section.id
                                            ? 'bg-indigo-600 text-white shadow-lg'
                                            : 'bg-gray-900/40 text-gray-300 hover:bg-gray-700/60'
                                    }`}
                                >
                                    <div className="font-semibold flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            {section.icon && (
                                                <MaterialSymbol
                                                    name={section.icon}
                                                    className="text-lg"
                                                />
                                            )}
                                            <span>{section.label}</span>
                                        </div>
                                        {section.id === 'support' && supportBadgeCount > 0 && (
                                            <span className="text-xs font-semibold bg-amber-500/20 text-amber-200 border border-amber-400/40 px-2 py-0.5 rounded-full">
                                                {supportBadgeCount}
                                            </span>
                                        )}
                                    </div>
                                    <div className={`text-xs ${activeSection === section.id ? 'text-indigo-100' : 'text-gray-400'}`}>
                                        {section.description}
                                    </div>
                                </button>
                            ))}
                        </nav>
                    </div>
                </aside>

                <main className="flex-1 p-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                        <div>
                            <h2 className="text-2xl font-semibold">{activeSectionMeta.label}</h2>
                            <p className="text-sm text-gray-400">{activeSectionMeta.description}</p>
                        </div>
                        {activeSection === 'dashboard' && (
                            <button
                                onClick={handleDevtoolsRefreshUsers}
                                className="self-start sm:self-auto text-sm bg-indigo-600 hover:bg-indigo-500 text-white font-semibold py-2 px-4 rounded-lg"
                            >
                                Refresh metrics
                            </button>
                        )}
                    </div>

                    {activeSection === 'dashboard' && (
                        <section className="mb-8">
                            <h3 className="text-lg font-semibold mb-4 text-gray-200">Dashboard Overview</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="group" className="text-gray-500" />
                                        <h4>Total Users</h4>
                                    </div>
                                    <p className="text-3xl font-bold mt-1">{dashboardStats.totalUsers}</p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="workspace_premium" className="text-gray-500" />
                                        <h4>Active Subscribers</h4>
                                    </div>
                                    <p className="text-3xl font-bold mt-1">{dashboardStats.activeSubscribers}</p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="photo_library" className="text-gray-500" />
                                        <h4>Total Images Generated</h4>
                                    </div>
                                    <p className="text-3xl font-bold mt-1">{dashboardStats.totalImagesGenerated}</p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="payments" className="text-gray-500" />
                                        <h4>Total Income</h4>
                                    </div>
                                    <p className="text-3xl font-bold mt-1">${dashboardStats.totalIncome.toFixed(2)}</p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="receipt_long" className="text-gray-500" />
                                        <h4>Total Cost</h4>
                                    </div>
                                    <p className="text-3xl font-bold mt-1">${dashboardStats.totalCost.toFixed(2)}</p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="trending_up" className="text-gray-500" />
                                        <h4>Net Profit</h4>
                                    </div>
                                    <p className={`text-3xl font-bold mt-1 ${dashboardStats.totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        ${dashboardStats.totalProfit.toFixed(2)}
                                    </p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="insights" className="text-gray-500" />
                                        <h4>Avg Images / User</h4>
                                    </div>
                                    <p className="text-3xl font-bold mt-1">{averageImagesPerUser.toFixed(1)}</p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="attach_money" className="text-gray-500" />
                                        <h4>CLV</h4>
                                    </div>
                                    <p className="text-3xl font-bold mt-1">${clv.toFixed(2)}</p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="percent" className="text-gray-500" />
                                        <h4>Profit Margin</h4>
                                    </div>
                                    <p className={`text-3xl font-bold mt-1 ${profitMargin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        {profitMargin.toFixed(1)}%
                                    </p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="memory" className="text-gray-500" />
                                        <h4>System Load</h4>
                                    </div>
                                    <p className="text-3xl font-bold mt-1">{dashboardStats.systemLoad}</p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="local_fire_department" className="text-gray-500" />
                                        <h4>Burn Rate</h4>
                                    </div>
                                    <p className="text-3xl font-bold mt-1 text-orange-300">
                                        ${burnRatePerUser.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-2">Avg. Cost per User</p>
                                </div>
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2 text-gray-400 text-sm font-medium">
                                        <MaterialSymbol name="balance" className="text-gray-500" />
                                        <h4>Net Profit / Loss</h4>
                                    </div>
                                    <p className={`text-3xl font-bold mt-1 ${breakEvenNet >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                        ${breakEvenNet.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-2">
                                        Target for Profit:{' '}
                                        {breakEvenNet < 0
                                            ? `${targetForProfit} more users`
                                            : 'On track'}
                                    </p>
                                </div>
                            </div>
                        </section>
                    )}

                    {activeSection === 'models' && (
                        <section className="mb-8">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold">Configuration</h3>
                                {(modelConfigLoading || modelListLoading || priceConfigLoading) && (
                                    <span className="text-sm text-gray-400">Loading...</span>
                                )}
                            </div>
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg space-y-5">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-base font-semibold">Gemini Models</h4>
                                        {(modelConfigLoading || modelListLoading) && (
                                            <span className="text-sm text-gray-400">Loading...</span>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Image Model</label>
                                        <select
                                            value={imageModel}
                                            onChange={(e) => setImageModel(e.target.value)}
                                            className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg appearance-none"
                                        >
                                            {imageModelOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">Vision Model</label>
                                        <select
                                            value={visionModel}
                                            onChange={(e) => setVisionModel(e.target.value)}
                                            className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg appearance-none"
                                        >
                                            {visionModelOptions.map((option) => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    {modelListError && <p className="text-sm text-yellow-300">{modelListError}</p>}
                                    {modelListWarning && <p className="text-sm text-yellow-300">{modelListWarning}</p>}
                                    {visionModelApiEmpty && (
                                        <p className="text-sm text-yellow-300">
                                            No vision-capable models detected from API response
                                        </p>
                                    )}
                                    {modelConfigError && <p className="text-sm text-red-400">{modelConfigError}</p>}
                                    <div className="flex items-center justify-between">
                                        <button
                                            onClick={handleSaveModels}
                                            disabled={isSavingModels}
                                            className="py-2 px-6 rounded-lg bg-indigo-600 hover:bg-indigo-500 font-bold text-white disabled:opacity-50"
                                        >
                                            {isSavingModels ? 'Saving...' : 'Save'}
                                        </button>
                                        {modelSaveStatus && (
                                            <span className={`text-sm ${modelSaveStatus === 'Saved' ? 'text-green-400' : 'text-red-400'}`}>
                                                {modelSaveStatus}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-gray-800 p-6 rounded-lg shadow-lg space-y-5">
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-base font-semibold">Price Per Image</h4>
                                        {priceConfigLoading && <span className="text-sm text-gray-400">Loading...</span>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-300 mb-2">USD per image</label>
                                        <input
                                            type="number"
                                            step="0.001"
                                            min="0"
                                            value={pricePerImage}
                                            onChange={(e) => setPricePerImage(e.target.value)}
                                            className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg"
                                        />
                                        <p className="text-xs text-gray-400 mt-2">Stored in app_config/KPIs.total_cost.</p>
                                    </div>
                                    {priceConfigError && <p className="text-sm text-red-400">{priceConfigError}</p>}
                                    <div className="flex items-center justify-between">
                                        <button
                                            onClick={handleSavePrice}
                                            disabled={isSavingPrice}
                                            className="py-2 px-6 rounded-lg bg-indigo-600 hover:bg-indigo-500 font-bold text-white disabled:opacity-50"
                                        >
                                            {isSavingPrice ? 'Saving...' : 'Save'}
                                        </button>
                                        {priceSaveStatus && (
                                            <span className={`text-sm ${priceSaveStatus === 'Saved' ? 'text-green-400' : 'text-red-400'}`}>
                                                {priceSaveStatus}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}

                    {activeSection === 'devtools' && (
                        <section className="mb-8">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold">Admin Debug Console</h3>
                                {modelRawLoading && <span className="text-sm text-gray-400">Loading...</span>}
                            </div>
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg space-y-4">
                                <div className="flex flex-wrap gap-3">
                                    <button
                                        onClick={handleDevtoolsRefreshUsers}
                                        className="py-2 px-4 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-semibold"
                                    >
                                        Refresh metrics
                                    </button>
                                    <button
                                        onClick={handleDevtoolsReloadModels}
                                        className="py-2 px-4 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-semibold"
                                    >
                                        Reload model list
                                    </button>
                                    <button
                                        onClick={handleDevtoolsFetchRawModels}
                                        className="py-2 px-4 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-semibold"
                                    >
                                        Fetch raw model response
                                    </button>
                                    <button
                                        onClick={handleDevtoolsCheckConfig}
                                        className="py-2 px-4 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-semibold"
                                    >
                                        Check model config
                                    </button>
                                    <button
                                        onClick={handleDevtoolsClear}
                                        className="py-2 px-4 rounded-lg bg-gray-700 hover:bg-gray-600 text-sm font-semibold"
                                    >
                                        Clear console
                                    </button>
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                                    <span>Endpoint: Google Generative Language API models list</span>
                                    {modelRawResponse?.generatedAt && (
                                        <span>Generated: {new Date(modelRawResponse.generatedAt).toLocaleString()}</span>
                                    )}
                                </div>
                                {devtoolsStatus && <p className="text-sm text-indigo-300">{devtoolsStatus}</p>}
                                {modelRawError && <p className="text-sm text-red-400">{modelRawError}</p>}
                                {modelRawWarning && <p className="text-sm text-yellow-300">{modelRawWarning}</p>}
                                <p className="text-xs text-gray-500">Run a tool to populate console output.</p>
                                <pre className="max-h-96 overflow-auto rounded-lg bg-gray-900 p-4 text-xs text-green-200">
                                    {devtoolsOutput}
                                </pre>
                            </div>
                        </section>
                    )}

                    {activeSection === 'users' && (
                        <section>
                            <h3 className="text-lg font-semibold mb-4">User Management</h3>
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg">
                                <input
                                    type="text"
                                    placeholder="Search by email..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full max-w-sm p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition mb-4"
                                />
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left text-gray-400">
                                        <thead className="text-xs text-gray-300 uppercase bg-gray-700">
                                            <tr>
                                                <TableHeader sortKey="email" requestSort={requestSort} sortConfig={sortConfig}>Email</TableHeader>
                                                <TableHeader sortKey="credits" requestSort={requestSort} sortConfig={sortConfig}>Credits</TableHeader>
                                                <TableHeader sortKey="images_generated" requestSort={requestSort} sortConfig={sortConfig}>Generated</TableHeader>
                                                <TableHeader sortKey="plan" requestSort={requestSort} sortConfig={sortConfig}>Plan</TableHeader>
                                                <TableHeader sortKey="role" requestSort={requestSort} sortConfig={sortConfig}>Role</TableHeader>
                                                <TableHeader sortKey="createdAt" requestSort={requestSort} sortConfig={sortConfig}>Joined</TableHeader>
                                                <th scope="col" className="px-6 py-3 text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedAndFilteredUsers.map(user => (
                                                <UserRow
                                                    key={user.id}
                                                    user={user}
                                                    onEdit={() => setEditingUser(user)}
                                                    onViewGallery={() => setViewingUser(user)}
                                                    onImpersonate={() => handleImpersonate(user)}
                                                />
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </section>
                    )}

                    {activeSection === 'audit' && (
                        <section>
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold">Admin Audit Log</h3>
                                    {auditLogsLoading && (
                                        <span className="text-sm text-gray-400">Loading...</span>
                                    )}
                                </div>
                                {auditLogsError && (
                                    <p className="text-sm text-red-400">{auditLogsError}</p>
                                )}
                                {!auditLogsLoading && auditLogs.length === 0 && (
                                    <p className="text-sm text-gray-400">No audit log entries yet.</p>
                                )}
                                {!auditLogsLoading && auditLogs.length > 0 && (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left text-gray-300 border border-gray-700">
                                            <thead className="text-xs uppercase text-gray-400 bg-gray-900/70">
                                                <tr>
                                                    <th scope="col" className="px-4 py-3">Timestamp</th>
                                                    <th scope="col" className="px-4 py-3">Action Type</th>
                                                    <th scope="col" className="px-4 py-3">Admin ID</th>
                                                    <th scope="col" className="px-4 py-3">Target User ID</th>
                                                    <th scope="col" className="px-4 py-3">Change Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {auditLogs.map((entry) => (
                                                    <tr key={entry.id} className="border-t border-gray-700">
                                                        <td className="px-4 py-3 whitespace-nowrap text-gray-300">
                                                            {formatTimestamp(entry.timestamp)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${actionBadgeClass(entry.actionType)}`}>
                                                                {entry.actionType || 'unknown'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-400">{entry.adminId || '—'}</td>
                                                        <td className="px-4 py-3 text-gray-400">{entry.targetUserId || '—'}</td>
                                                        <td className="px-4 py-3 text-gray-300">
                                                            {formatChangeDescription(entry)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </section>
                    )}

                    {activeSection === 'support' && (
                        <section className="space-y-6">
                            <div className="bg-gray-800 p-6 rounded-lg shadow-lg space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold">Ticket Inbox</h3>
                                    {supportLoading && (
                                        <span className="text-sm text-gray-400">Loading tickets...</span>
                                    )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <select
                                        value={supportFilters.status}
                                        onChange={(event) =>
                                            setSupportFilters((prev) => ({
                                                ...prev,
                                                status: event.target.value,
                                            }))
                                        }
                                        className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg appearance-none text-sm"
                                    >
                                        <option value="all">All statuses</option>
                                        {Object.values(SUPPORT_STATUSES).map((status) => (
                                            <option key={status} value={status}>
                                                {SUPPORT_STATUS_LABELS[status]}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={supportFilters.priority}
                                        onChange={(event) =>
                                            setSupportFilters((prev) => ({
                                                ...prev,
                                                priority: event.target.value,
                                            }))
                                        }
                                        className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg appearance-none text-sm"
                                    >
                                        <option value="all">All priorities</option>
                                        {SUPPORT_PRIORITIES.map((priority) => (
                                            <option key={priority} value={priority}>
                                                {priority}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={supportFilters.category}
                                        onChange={(event) =>
                                            setSupportFilters((prev) => ({
                                                ...prev,
                                                category: event.target.value,
                                            }))
                                        }
                                        className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg appearance-none text-sm"
                                    >
                                        <option value="all">All categories</option>
                                        {SUPPORT_CATEGORIES.map((category) => (
                                            <option key={category} value={category}>
                                                {category}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="text"
                                        value={supportFilters.search}
                                        onChange={(event) =>
                                            setSupportFilters((prev) => ({
                                                ...prev,
                                                search: event.target.value,
                                            }))
                                        }
                                        placeholder="Search subject or user"
                                        className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-sm"
                                    />
                                </div>
                                {supportError && (
                                    <p className="text-sm text-red-400">{supportError}</p>
                                )}
                            </div>

                            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                                <div className="bg-gray-800 p-4 rounded-lg shadow-lg space-y-3">
                                    {filteredSupportTickets.length === 0 && !supportLoading && (
                                        <p className="text-sm text-gray-400">
                                            No tickets match the current filters.
                                        </p>
                                    )}
                                    {filteredSupportTickets.map((ticket) => (
                                        <button
                                            key={ticket.id}
                                            type="button"
                                            onClick={() => setSelectedSupportTicketId(ticket.id)}
                                            className={`w-full text-left p-4 rounded-lg border transition ${
                                                selectedSupportTicketId === ticket.id
                                                    ? 'border-indigo-500 bg-gray-900/80'
                                                    : 'border-transparent bg-gray-900/40 hover:border-gray-600'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="font-semibold truncate">{ticket.subject}</div>
                                                <span
                                                    className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(
                                                        getDisplayStatus(ticket)
                                                    )}`}
                                                >
                                                    {SUPPORT_STATUS_LABELS[getDisplayStatus(ticket)] || 'Open'}
                                                </span>
                                            </div>
                                            <div className="text-xs text-gray-400 mt-2 space-y-1">
                                                <div>
                                                    {ticket.userDisplayName || ticket.userEmail || 'Unknown user'}
                                                </div>
                                                <div>Updated: {formatTimestamp(ticket.updatedAt)}</div>
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                <div className="xl:col-span-2 bg-gray-800 p-6 rounded-lg shadow-lg space-y-6">
                                    {!selectedSupportTicket && (
                                        <p className="text-sm text-gray-400">
                                            Select a ticket to view details and reply.
                                        </p>
                                    )}
                                    {selectedSupportTicket && (
                                        <>
                                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                                <div>
                                                    <h3 className="text-xl font-semibold">
                                                        {selectedSupportTicket.subject}
                                                    </h3>
                                                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400 mt-2">
                                                        <span
                                                            className={`text-xs px-2 py-0.5 rounded-full ${statusBadgeClass(
                                                                selectedSupportDisplayStatus
                                                            )}`}
                                                        >
                                                            {SUPPORT_STATUS_LABELS[selectedSupportDisplayStatus] ||
                                                                'Open'}
                                                        </span>
                                                        {selectedSupportTicket.category && (
                                                            <span>Category: {selectedSupportTicket.category}</span>
                                                        )}
                                                        {selectedSupportTicket.priority && (
                                                            <span>Priority: {selectedSupportTicket.priority}</span>
                                                        )}
                                                    </div>
                                                    <div className="text-xs text-gray-500 mt-2 space-y-1">
                                                        <div>
                                                            User: {selectedSupportTicket.userEmail || 'N/A'}
                                                        </div>
                                                        <div>
                                                            Created: {formatTimestamp(selectedSupportTicket.createdAt)}
                                                        </div>
                                                        <div>
                                                            Updated: {formatTimestamp(selectedSupportTicket.updatedAt)}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex flex-wrap gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={handleAssignToSelf}
                                                        disabled={supportActionLoading}
                                                        className="bg-gray-700 hover:bg-gray-600 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                                                    >
                                                        Assign to me
                                                    </button>
                                                    {[SUPPORT_STATUSES.RESOLVED, SUPPORT_STATUSES.CLOSED].includes(
                                                        selectedSupportTicket.status
                                                    ) ? (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                handleSupportStatusChange(SUPPORT_STATUSES.OPEN)
                                                            }
                                                            disabled={supportActionLoading}
                                                            className="bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                                                        >
                                                            Reopen
                                                        </button>
                                                    ) : (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleSupportStatusChange(
                                                                        SUPPORT_STATUSES.RESOLVED
                                                                    )
                                                                }
                                                                disabled={supportActionLoading}
                                                                className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                                                            >
                                                                Mark resolved
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handleSupportStatusChange(
                                                                        SUPPORT_STATUSES.CLOSED
                                                                    )
                                                                }
                                                                disabled={supportActionLoading}
                                                                className="bg-gray-600 hover:bg-gray-500 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
                                                            >
                                                                Close
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="space-y-4">
                                                {supportMessagesLoading && (
                                                    <p className="text-sm text-gray-400">Loading messages...</p>
                                                )}
                                                {supportMessagesError && (
                                                    <p className="text-sm text-red-400">{supportMessagesError}</p>
                                                )}
                                                {!supportMessagesLoading &&
                                                    supportMessages.map((msg) => (
                                                        <div
                                                            key={msg.id}
                                                            className={`p-4 rounded-lg border ${
                                                                msg.authorType === 'admin'
                                                                    ? 'bg-indigo-500/10 border-indigo-400/40'
                                                                    : 'bg-gray-900/50 border-gray-700'
                                                            }`}
                                                        >
                                                            <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                                                                <span className="uppercase tracking-wide">
                                                                    {msg.authorType === 'admin'
                                                                        ? 'Admin'
                                                                        : 'User'}
                                                                </span>
                                                                <span>{formatTimestamp(msg.createdAt)}</span>
                                                            </div>
                                                            <p className="text-sm text-gray-200 whitespace-pre-wrap">
                                                                {msg.body}
                                                            </p>
                                                        </div>
                                                    ))}
                                            </div>

                                            <div className="border-t border-gray-700 pt-4 space-y-3">
                                                <label className="text-sm font-medium text-gray-300">
                                                    Reply to user
                                                </label>
                                                <textarea
                                                    value={supportReplyBody}
                                                    onChange={(event) => setSupportReplyBody(event.target.value)}
                                                    rows={4}
                                                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                                                    placeholder="Write a response"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleSupportReply}
                                                    disabled={supportActionLoading}
                                                    className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60"
                                                >
                                                    {supportActionLoading ? 'Sending...' : 'Send reply'}
                                                </button>
                                            </div>

                                            <div className="border-t border-gray-700 pt-4 space-y-3">
                                                <div className="flex items-center justify-between">
                                                    <h4 className="text-sm font-semibold text-gray-200">
                                                        Internal notes
                                                    </h4>
                                                    {supportNotesLoading && (
                                                        <span className="text-xs text-gray-500">Loading...</span>
                                                    )}
                                                </div>
                                                {supportNotes.length === 0 && (
                                                    <p className="text-xs text-gray-500">
                                                        No internal notes yet.
                                                    </p>
                                                )}
                                                <div className="space-y-2">
                                                    {supportNotes.map((note) => (
                                                        <div
                                                            key={note.id}
                                                            className="text-sm text-gray-200 bg-gray-900/60 border border-gray-700 rounded-lg p-3"
                                                        >
                                                            <div className="text-xs text-gray-500 mb-1">
                                                                {formatTimestamp(note.createdAt)}
                                                            </div>
                                                            <p className="whitespace-pre-wrap">{note.body}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                                <textarea
                                                    value={supportNoteBody}
                                                    onChange={(event) => setSupportNoteBody(event.target.value)}
                                                    rows={3}
                                                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                                                    placeholder="Add an internal note (admins only)"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleAddNote}
                                                    disabled={supportActionLoading}
                                                    className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-5 py-2.5 rounded-lg disabled:opacity-60"
                                                >
                                                    Add note
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </section>
                    )}

                    {activeSection === 'playground' && (
                        <div className="space-y-10">
                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Typography</h3>
                                    <p className="text-xs text-gray-500">Component: Admin text styles (admin.jsx)</p>
                                </div>
                                <div className="mt-4 space-y-3">
                                    <h1 className="text-3xl font-bold">Heading 1 — Admin headline</h1>
                                    <h2 className="text-2xl font-semibold text-gray-100">Heading 2 — Section title</h2>
                                    <h3 className="text-xl font-semibold text-gray-200">Heading 3 — Subsection label</h3>
                                    <p className="text-sm text-gray-200">
                                        Body text sample showing the default admin color and line-height.
                                        <span className="text-gray-400"> Muted text example for helper copy.</span>
                                        <a href="#playground-link" className="text-indigo-300 hover:text-indigo-200 ml-1">
                                            Inline link
                                        </a>
                                    </p>
                                    <p className="text-sm text-gray-400">
                                        Long text wrapping example: This is a deliberately long string that should wrap
                                        cleanly across multiple lines on smaller screens without breaking the layout.
                                    </p>
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Buttons</h3>
                                    <p className="text-xs text-gray-500">Component: Admin buttons (admin.jsx)</p>
                                </div>
                                <div className="mt-5 grid gap-4 lg:grid-cols-2">
                                    <div className="space-y-3">
                                        <p className="text-xs uppercase tracking-wider text-gray-500">Variants</p>
                                        <div className="flex flex-wrap gap-3">
                                            <button className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-5 py-2.5 rounded-lg">
                                                Primary
                                            </button>
                                            <button className="bg-gray-600 hover:bg-gray-500 text-white font-semibold px-5 py-2.5 rounded-lg">
                                                Secondary
                                            </button>
                                            <button className="bg-red-600 hover:bg-red-500 text-white font-semibold px-5 py-2.5 rounded-lg">
                                                Destructive
                                            </button>
                                            <button className="border border-gray-500 text-gray-200 hover:bg-gray-700/60 font-semibold px-5 py-2.5 rounded-lg">
                                                Outline
                                            </button>
                                            <button className="text-gray-200 hover:text-white hover:bg-gray-700/40 font-semibold px-5 py-2.5 rounded-lg">
                                                Ghost
                                            </button>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <p className="text-xs uppercase tracking-wider text-gray-500">States & sizes</p>
                                        <div className="flex flex-wrap gap-3">
                                            <button disabled className="bg-indigo-600/60 text-white font-semibold px-5 py-2.5 rounded-lg cursor-not-allowed">
                                                Disabled
                                            </button>
                                            <button
                                                onClick={handlePlaygroundLoading}
                                                className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2"
                                            >
                                                {isPlaygroundLoading ? (
                                                    <>
                                                        <span className="h-4 w-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                                                        Loading
                                                    </>
                                                ) : (
                                                    'Trigger Loading'
                                                )}
                                            </button>
                                            <button className="bg-gray-700 hover:bg-gray-600 text-white font-semibold p-2.5 rounded-lg">
                                                <MaterialSymbol name="settings" className="text-lg" />
                                            </button>
                                            <button className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg">
                                                Small
                                            </button>
                                            <button className="bg-indigo-600 hover:bg-indigo-500 text-white text-base font-semibold px-5 py-2.5 rounded-lg">
                                                Medium
                                            </button>
                                            <button className="bg-indigo-600 hover:bg-indigo-500 text-white text-lg font-semibold px-6 py-3 rounded-lg">
                                                Large
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Form Elements</h3>
                                    <p className="text-xs text-gray-500">Component: Inputs & form controls (admin.jsx)</p>
                                </div>
                                <div className="mt-5 grid gap-6 lg:grid-cols-2">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Text input</label>
                                            <input
                                                type="text"
                                                value={playgroundForm.name}
                                                onChange={(event) => setPlaygroundForm((prev) => ({ ...prev, name: event.target.value }))}
                                                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                                                placeholder="Jane Doe"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">Helper: Uses the default input style.</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Text input (error)</label>
                                            <input
                                                type="text"
                                                value={playgroundForm.email}
                                                onChange={(event) => setPlaygroundForm((prev) => ({ ...prev, email: event.target.value }))}
                                                className="w-full p-3 bg-gray-700 border border-red-500 rounded-lg focus:ring-2 focus:ring-red-400 transition"
                                                placeholder="email@example.com"
                                            />
                                            <p className="text-xs text-red-300 mt-1">Validation: Please enter a valid email.</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Text input (success)</label>
                                            <input
                                                type="text"
                                                defaultValue="Saved ✅"
                                                className="w-full p-3 bg-gray-700 border border-green-500 rounded-lg focus:ring-2 focus:ring-green-400 transition"
                                            />
                                            <p className="text-xs text-green-300 mt-1">Validation: Looks good.</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Password input</label>
                                            <input
                                                type="password"
                                                value={playgroundForm.password}
                                                onChange={(event) => setPlaygroundForm((prev) => ({ ...prev, password: event.target.value }))}
                                                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                                                placeholder="••••••••"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Textarea</label>
                                            <textarea
                                                value={playgroundForm.message}
                                                onChange={(event) => setPlaygroundForm((prev) => ({ ...prev, message: event.target.value }))}
                                                rows={3}
                                                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 transition"
                                                placeholder="Write a quick note..."
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">Select / Dropdown</label>
                                            <select
                                                value={playgroundForm.status}
                                                onChange={(event) => setPlaygroundForm((prev) => ({ ...prev, status: event.target.value }))}
                                                className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg appearance-none"
                                            >
                                                <option value="active">Active</option>
                                                <option value="pending">Pending</option>
                                                <option value="disabled">Disabled</option>
                                            </select>
                                        </div>
                                        <div className="flex flex-col gap-3">
                                            <span className="text-sm font-medium text-gray-300">Checkbox & toggle</span>
                                            <label className="flex items-center gap-3 text-sm text-gray-200">
                                                <input type="checkbox" className="h-4 w-4 rounded border-gray-500 bg-gray-700" defaultChecked />
                                                Weekly digest
                                            </label>
                                            <label className="flex items-center gap-3 text-sm text-gray-200">
                                                <input
                                                    type="checkbox"
                                                    checked={playgroundForm.notifications}
                                                    onChange={(event) => setPlaygroundForm((prev) => ({ ...prev, notifications: event.target.checked }))}
                                                    className="peer sr-only"
                                                />
                                                <div className="w-11 h-6 bg-gray-700 rounded-full peer-checked:bg-indigo-600 relative transition">
                                                    <div className="absolute top-0.5 left-0.5 h-5 w-5 bg-white rounded-full transition peer-checked:translate-x-5" />
                                                </div>
                                                Enable alerts
                                            </label>
                                        </div>
                                        <div>
                                            <span className="text-sm font-medium text-gray-300 block mb-2">Radio group</span>
                                            <div className="flex flex-wrap gap-4 text-sm text-gray-200">
                                                <label className="flex items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        name="playground-radio"
                                                        value="option-a"
                                                        checked={playgroundForm.radioChoice === 'option-a'}
                                                        onChange={(event) => setPlaygroundForm((prev) => ({ ...prev, radioChoice: event.target.value }))}
                                                    />
                                                    Option A
                                                </label>
                                                <label className="flex items-center gap-2">
                                                    <input
                                                        type="radio"
                                                        name="playground-radio"
                                                        value="option-b"
                                                        checked={playgroundForm.radioChoice === 'option-b'}
                                                        onChange={(event) => setPlaygroundForm((prev) => ({ ...prev, radioChoice: event.target.value }))}
                                                    />
                                                    Option B
                                                </label>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                                Slider: {playgroundForm.sliderValue}%
                                            </label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="100"
                                                value={playgroundForm.sliderValue}
                                                onChange={(event) => setPlaygroundForm((prev) => ({ ...prev, sliderValue: Number(event.target.value) }))}
                                                className="w-full accent-indigo-500"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">Helper: Range input used in settings.</p>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Modals & Dialogs</h3>
                                    <p className="text-xs text-gray-500">Component: Admin modals (admin.jsx)</p>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    <button
                                        onClick={() => setShowPlaygroundModal(true)}
                                        className="bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-5 py-2.5 rounded-lg"
                                    >
                                        Open Modal
                                    </button>
                                    <button
                                        onClick={() => setShowConfirmDialog(true)}
                                        className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-5 py-2.5 rounded-lg"
                                    >
                                        Open Confirm Dialog
                                    </button>
                                    <p className="text-xs text-gray-400 flex items-center gap-2">
                                        <MaterialSymbol name="keyboard" className="text-base" />
                                        Tip: Press Esc to close (focus lands on primary action).
                                    </p>
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Toasts & Notifications</h3>
                                    <p className="text-xs text-gray-500">Component: SupportToast (support-ui.jsx)</p>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    <button
                                        onClick={() => setPlaygroundToast({ tone: 'success', message: 'Success toast triggered.' })}
                                        className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-4 py-2 rounded-lg"
                                    >
                                        Success
                                    </button>
                                    <button
                                        onClick={() => setPlaygroundToast({ tone: 'error', message: 'Error toast triggered.' })}
                                        className="bg-red-600 hover:bg-red-500 text-white font-semibold px-4 py-2 rounded-lg"
                                    >
                                        Error
                                    </button>
                                    <button
                                        onClick={() => setPlaygroundToast({ tone: 'info', message: 'Info toast triggered.' })}
                                        className="bg-gray-700 hover:bg-gray-600 text-white font-semibold px-4 py-2 rounded-lg"
                                    >
                                        Info
                                    </button>
                                    <button
                                        onClick={() => setPlaygroundToast({ tone: 'warning', message: 'Warning toast triggered.' })}
                                        className="bg-amber-500 hover:bg-amber-400 text-gray-900 font-semibold px-4 py-2 rounded-lg"
                                    >
                                        Warning
                                    </button>
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Badges & Pills</h3>
                                    <p className="text-xs text-gray-500">Component: Status badges (support-ui.jsx)</p>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-700 text-gray-200 border border-gray-600">
                                        Neutral
                                    </span>
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-200 border border-emerald-400/40">
                                        Success
                                    </span>
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-200 border border-amber-400/40">
                                        Warning
                                    </span>
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-500/20 text-red-200 border border-red-400/40">
                                        Error
                                    </span>
                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-500 text-gray-900">
                                        Credits: 12
                                    </span>
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6 space-y-5">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Cards & Panels</h3>
                                    <p className="text-xs text-gray-500">Component: Admin cards (admin.jsx)</p>
                                </div>
                                <div className="grid gap-4 lg:grid-cols-2">
                                    <div className="bg-gray-800 p-5 rounded-lg border border-gray-700 shadow-lg">
                                        <h4 className="font-semibold text-gray-100 mb-2">Standard Card</h4>
                                        <p className="text-sm text-gray-400">
                                            Use cards for KPI highlights, summaries, and grouped data.
                                        </p>
                                    </div>
                                    <div className="bg-gray-900/60 p-5 rounded-lg border border-gray-700">
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-semibold text-gray-100">Settings Panel</h4>
                                            <button className="text-xs text-indigo-300 hover:text-indigo-200">Edit</button>
                                        </div>
                                        <div className="mt-3 space-y-2 text-sm text-gray-400">
                                            <div className="flex items-center justify-between">
                                                <span>Daily summary</span>
                                                <span className="text-green-300">Enabled</span>
                                            </div>
                                            <div className="flex items-center justify-between border-t border-gray-700 pt-2">
                                                <span>Weekly digest</span>
                                                <span className="text-gray-300">Paused</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="border-t border-gray-700 pt-4 text-sm text-gray-400">
                                    Divider / section separator example.
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6 space-y-5">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Tables & Lists</h3>
                                    <p className="text-xs text-gray-500">Component: Admin tables (admin.jsx)</p>
                                </div>
                                <div className="overflow-x-auto rounded-lg border border-gray-700">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-gray-900/60 text-gray-400">
                                            <tr>
                                                <th className="px-4 py-3 text-left">ID</th>
                                                <th className="px-4 py-3 text-left">Name</th>
                                                <th className="px-4 py-3 text-left">Status</th>
                                                <th className="px-4 py-3 text-left">Role</th>
                                                <th className="px-4 py-3 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-800">
                                            {playgroundTableRows.map((row) => (
                                                <tr key={row.id} className="bg-gray-800/40">
                                                    <td className="px-4 py-3 text-gray-200">{row.id}</td>
                                                    <td className="px-4 py-3 text-gray-200">{row.name}</td>
                                                    <td className="px-4 py-3">
                                                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-gray-700 text-gray-200">
                                                            {row.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-gray-400">{row.role}</td>
                                                    <td className="px-4 py-3 text-right">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <button className="text-indigo-300 hover:text-indigo-200" aria-label="View">
                                                                <MaterialSymbol name="visibility" className="text-lg" />
                                                            </button>
                                                            <button className="text-emerald-300 hover:text-emerald-200" aria-label="Edit">
                                                                <MaterialSymbol name="edit_square" className="text-lg" />
                                                            </button>
                                                            <button className="text-red-300 hover:text-red-200" aria-label="Delete">
                                                                <MaterialSymbol name="delete" className="text-lg" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="grid gap-4 lg:grid-cols-3">
                                    <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4 text-sm text-gray-400">
                                        <h4 className="font-semibold text-gray-200 mb-2">Empty state</h4>
                                        <p>No records found. Try adjusting filters.</p>
                                    </div>
                                    <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-200 mb-3 text-sm">Loading skeleton</h4>
                                        <div className="space-y-2 animate-pulse">
                                            <div className="h-3 bg-gray-700 rounded" />
                                            <div className="h-3 bg-gray-700 rounded w-3/4" />
                                            <div className="h-3 bg-gray-700 rounded w-1/2" />
                                        </div>
                                    </div>
                                    <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
                                        <h4 className="font-semibold text-gray-200 mb-3 text-sm">Paginated list</h4>
                                        <ul className="space-y-2 text-sm text-gray-300">
                                            <li className="flex items-center justify-between">
                                                <span>Mock item 1</span>
                                                <span className="text-xs text-gray-500">Updated 2m</span>
                                            </li>
                                            <li className="flex items-center justify-between">
                                                <span>Mock item 2</span>
                                                <span className="text-xs text-gray-500">Updated 1h</span>
                                            </li>
                                            <li className="flex items-center justify-between">
                                                <span>Mock item 3</span>
                                                <span className="text-xs text-gray-500">Updated 1d</span>
                                            </li>
                                        </ul>
                                        <div className="flex items-center justify-between mt-4 text-xs text-gray-400">
                                            <button className="px-3 py-1 rounded-md bg-gray-700">Prev</button>
                                            <span>Page 1 of 4</span>
                                            <button className="px-3 py-1 rounded-md bg-gray-700">Next</button>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6 space-y-5">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Accordions & Steps</h3>
                                    <p className="text-xs text-gray-500">Component: Generator steps (App.jsx)</p>
                                </div>
                                <div className="grid gap-4 lg:grid-cols-2">
                                    <div className="space-y-2">
                                        {[1, 2, 3].map((item) => {
                                            const isOpen = playgroundAccordion === item;
                                            return (
                                                <div key={item} className="bg-gray-900/50 border border-gray-700 rounded-lg">
                                                    <button
                                                        type="button"
                                                        onClick={() => setPlaygroundAccordion(isOpen ? 0 : item)}
                                                        className="w-full flex items-center justify-between px-4 py-3 text-left"
                                                    >
                                                        <span className="font-semibold text-gray-200">Accordion item {item}</span>
                                                        <MaterialSymbol
                                                            name="expand_more"
                                                            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                                        />
                                                    </button>
                                                    {isOpen && (
                                                        <div className="px-4 pb-4 text-sm text-gray-400">
                                                            This panel mirrors the expandable sections used in the generator flow.
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    <div className="space-y-3">
                                        {[1, 2, 3].map((step) => {
                                            const isComplete = step < playgroundStep;
                                            const isActive = step === playgroundStep;
                                            return (
                                                <button
                                                    key={step}
                                                    type="button"
                                                    onClick={() => setPlaygroundStep(step)}
                                                    className={`w-full flex items-center justify-between gap-3 rounded-lg px-4 py-3 text-left transition ${
                                                        isActive ? 'bg-gray-900/60' : 'bg-gray-900/30 hover:bg-gray-900/60'
                                                    }`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <span
                                                            className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${
                                                                isComplete
                                                                    ? 'bg-green-500/20 text-green-300 border border-green-400/50'
                                                                    : 'bg-gray-700 text-gray-200 border border-gray-600'
                                                            }`}
                                                        >
                                                            {step}
                                                        </span>
                                                        <div>
                                                            <p className="font-semibold text-gray-100">Step {step}</p>
                                                            <p className="text-xs text-gray-400">Short summary for this step.</p>
                                                        </div>
                                                    </div>
                                                    <MaterialSymbol name="chevron_right" className="text-gray-500" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6 space-y-5">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Tooltips</h3>
                                    <p className="text-xs text-gray-500">Component: Tooltip utility (admin.jsx)</p>
                                </div>
                                <div className="flex flex-wrap gap-6">
                                    {['info', 'help', 'visibility'].map((icon) => (
                                        <div key={icon} className="relative group">
                                            <button className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg">
                                                <MaterialSymbol name={icon} className="text-lg" />
                                            </button>
                                            <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-max bg-gray-900 text-xs text-gray-200 px-3 py-1 rounded-lg opacity-0 pointer-events-none group-hover:opacity-100 transition">
                                                Tooltip for {icon}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6 space-y-5">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Icons</h3>
                                    <p className="text-xs text-gray-500">Component: MaterialSymbol (admin.jsx)</p>
                                </div>
                                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                                    {playgroundIcons.map((icon) => (
                                        <div key={icon} className="bg-gray-900/60 border border-gray-700 rounded-lg p-3 flex flex-col items-center gap-2">
                                            <MaterialSymbol name={icon} className="text-2xl text-indigo-200" />
                                            <span className="text-xs text-gray-400">{icon}</span>
                                        </div>
                                    ))}
                                </div>
                            </section>

                            <section className="bg-gray-800/60 border border-gray-700 rounded-xl p-6 space-y-5">
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-lg font-semibold text-gray-200">Layout Utilities & Spacing</h3>
                                    <p className="text-xs text-gray-500">Component: Admin spacing presets (admin.jsx)</p>
                                </div>
                                <div className="grid gap-4 lg:grid-cols-2">
                                    <div className="space-y-3">
                                        <p className="text-sm text-gray-300 font-semibold">Padding scale</p>
                                        <div className="grid grid-cols-3 gap-3 text-xs text-gray-400">
                                            {['p-2', 'p-4', 'p-6'].map((padding) => (
                                                <div key={padding} className="bg-gray-900/60 border border-gray-700 rounded-lg">
                                                    <div className={`bg-indigo-500/20 border border-indigo-400/30 rounded-lg ${padding}`}>
                                                        {padding}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <p className="text-sm text-gray-300 font-semibold">Responsive columns</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs text-gray-300">
                                            <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">Column A</div>
                                            <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">Column B</div>
                                            <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-3">Column C</div>
                                        </div>
                                    </div>
                                </div>
                            </section>
                        </div>
                    )}
                </main>
            </div>

            {editingUser && (
                <EditUserModal
                    user={editingUser}
                    onClose={() => setEditingUser(null)}
                    onSave={fetchUsers}
                    logAdminAction={logAdminAction}
                />
            )}
            {viewingUser && <UserGalleryModal user={viewingUser} onClose={() => setViewingUser(null)} />}
            {showPlaygroundModal && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-lg">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xl font-semibold">Playground Modal</h3>
                            <button
                                type="button"
                                onClick={() => setShowPlaygroundModal(false)}
                                className="text-gray-400 hover:text-gray-200"
                                aria-label="Close modal"
                            >
                                <MaterialSymbol name="close" className="text-xl" />
                            </button>
                        </div>
                        <p className="text-sm text-gray-400">
                            This modal showcases the admin overlay styling, focus rings, and dark theme treatment.
                        </p>
                        <div className="mt-6 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowPlaygroundModal(false)}
                                className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                autoFocus
                                onClick={() => setShowPlaygroundModal(false)}
                                className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold"
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {showConfirmDialog && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                    <div className="bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h3 className="text-xl font-semibold mb-2">Confirm action</h3>
                        <p className="text-sm text-gray-400 mb-6">
                            This confirms the admin dialog pattern without writing any data.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => setShowConfirmDialog(false)}
                                className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                autoFocus
                                onClick={() => setShowConfirmDialog(false)}
                                className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-semibold"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <SupportToast toast={supportToast} onDismiss={() => setSupportToast(null)} />
            <SupportToast toast={playgroundToast} onDismiss={() => setPlaygroundToast(null)} />
        </div>
    );
}

// --- Helper Components ---
const SortIcon = ({ direction }) => {
    if (!direction) return <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1.5 opacity-30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" /></svg>;
    if (direction === 'ascending') return <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>;
    return <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>;
};

const TableHeader = ({ sortKey, requestSort, sortConfig, children }) => (
    <th scope="col" className="px-6 py-3 cursor-pointer select-none" onClick={() => requestSort(sortKey)}>
        <div className="flex items-center">
            {children}
            <SortIcon direction={sortConfig.key === sortKey ? sortConfig.direction : null} />
        </div>
    </th>
);

function UserRow({ user, onEdit, onViewGallery, onImpersonate }) {
    const joinDate = user.createdAt?.toDate ? user.createdAt.toDate().toLocaleDateString() : 'N/A';
    return (
        <tr className="bg-gray-800 border-b border-gray-700 hover:bg-gray-700/50">
            <td className="px-6 py-4 font-medium text-white">{user.email}</td>
            <td className="px-6 py-4 text-yellow-400 font-bold">{user.credits}</td>
            <td className="px-6 py-4">{user.images_generated || 0}</td>
            <td className="px-6 py-4">{user.plan}</td>
            <td className="px-6 py-4">
                 <span className={`px-2 py-1 text-xs font-semibold rounded-full ${user.role === 'admin' ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-300'}`}>
                    {user.role}
                </span>
            </td>
            <td className="px-6 py-4">{joinDate}</td>
            <td className="px-6 py-4">
                <div className="flex items-center justify-center gap-3">
                    <button
                        type="button"
                        onClick={onEdit}
                        title="Edit User"
                        aria-label="Edit User"
                        className="text-indigo-300 hover:opacity-70 transition-opacity cursor-pointer"
                    >
                        <MaterialSymbol name="edit_square" className="text-lg" />
                    </button>
                    <button
                        type="button"
                        onClick={onViewGallery}
                        title="View Gallery"
                        aria-label="View Gallery"
                        className="text-purple-300 hover:opacity-70 transition-opacity cursor-pointer"
                    >
                        <MaterialSymbol name="photo_library" className="text-lg" />
                    </button>
                    <button
                        type="button"
                        onClick={onImpersonate}
                        title="Impersonate"
                        aria-label="Impersonate"
                        className="text-amber-300 hover:opacity-70 transition-opacity cursor-pointer"
                    >
                        <MaterialSymbol name="domino_mask" className="text-lg" />
                    </button>
                </div>
            </td>
        </tr>
    );
}

function EditUserModal({ user, onClose, onSave, logAdminAction }) {
    const [credits, setCredits] = useState(user.credits || 0);
    const [role, setRole] = useState(user.role || 'user');
    const [plan, setPlan] = useState(user.plan || 'Free');
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = async () => {
        setIsSaving(true);
        const userDocRef = doc(db, 'users', user.id);
        try {
            const oldValue = {
                credits: user.credits ?? 0,
                role: user.role ?? 'user',
                plan: user.plan ?? 'Free',
            };
            const newValue = {
                credits: Number(credits),
                role: role,
                plan: plan,
            };
            await updateDoc(userDocRef, {
                credits: Number(credits),
                role: role,
                plan: plan
            });
            await logAdminAction?.({
                actionType: 'user_update',
                targetUserId: user.id,
                oldValue,
                newValue,
            });
            onSave();
            onClose();
        } catch (error) {
            console.error("Failed to update user:", error);
            alert("Error saving user data.");
        } finally {
            setIsSaving(false);
        }
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 p-8 rounded-lg shadow-xl w-full max-w-md">
                <h3 className="text-xl font-bold mb-2">Edit User</h3>
                <p className="text-sm text-gray-400 mb-6">{user.email}</p>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Credits</label>
                        <input 
                            type="number" 
                            value={credits} 
                            onChange={(e) => setCredits(e.target.value)} 
                            className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Plan</label>
                        <select 
                            value={plan}
                            onChange={(e) => setPlan(e.target.value)}
                            className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg appearance-none"
                        >
                            <option value="Free">Free</option>
                            <option value="Premium">Premium</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Role</label>
                        <select 
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg appearance-none"
                        >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>
                </div>
                <div className="flex justify-end gap-4 mt-8">
                    <button onClick={onClose} className="py-2 px-5 rounded-lg bg-gray-600 hover:bg-gray-500 font-semibold">Cancel</button>
                    <button onClick={handleSave} disabled={isSaving} className="py-2 px-5 rounded-lg bg-indigo-600 hover:bg-indigo-500 font-bold text-white disabled:opacity-50">
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function UserGalleryModal({ user, onClose }) {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const galleryRef = collection(db, 'users', user.id, 'gallery');
        const q = query(galleryRef, orderBy('createdAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setImages(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setLoading(false);
        }, (err) => {
            console.error("Error fetching user gallery:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user.id]);

    const handleDeleteImage = async (image) => {
        if (!window.confirm("Are you sure you want to permanently delete this image? This cannot be undone.")) return;
        
        try {
            // First, delete the Firestore document to remove it from the gallery view
            const docRef = doc(db, 'users', user.id, 'gallery', image.id);
            await deleteDoc(docRef);

            if (image.shortId) {
                const sharedRef = doc(db, 'shared_links', image.shortId);
                await deleteDoc(sharedRef);
            }

            // Then, create a reference to the file in Storage from the full URL and delete it.
            const storageRef = ref(storage, image.imageUrl);
            await deleteObject(storageRef);
            
        } catch (error) {
            // If the error is 'storage/object-not-found', it means the file was already deleted or the URL was invalid, which is okay.
            if (error.code === 'storage/object-not-found') {
                console.warn("Storage object not found, but proceeding since DB entry is deleted.", image.imageUrl);
            } else {
                console.error("Error deleting image:", error);
                alert("Failed to delete the image from storage. It has been removed from the gallery, but the file may still exist.");
            }
        }
    };
    
    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-gray-700">
                    <h2 className="text-xl font-bold text-white">Gallery for {user.email}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                <div className="p-6 overflow-y-auto">
                    {loading && <p>Loading gallery...</p>}
                    {!loading && images.length === 0 && <p className="text-gray-500 text-center">This user has no images in their gallery.</p>}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                        {images.map(image => (
                            <div key={image.id} className="relative group aspect-[4/5]">
                                <img src={image.imageUrl} alt="Generated" className="object-cover w-full h-full rounded-md" />
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all flex items-center justify-center">
                                    <button 
                                        onClick={() => handleDeleteImage(image)} 
                                        className="opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 hover:bg-red-700 text-white rounded-full p-2"
                                        title="Delete Image Permanently"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
